// Области видимости шаблона с проверкой изменений, как $rootScope в AngularJS 1.2.
// Дочерняя область наследует родительскую через прототип: шаблоны и подмены доставок
// рассчитывают именно на такое наследование.
import { equals } from './filters';
import { reportError } from './report';

const INIT = Symbol('init');
const TTL = 10;

let nextId = 1;

export function copy(source) {
    if (source === null || typeof source !== 'object') {
        return source;
    }
    if (source instanceof Date) {
        return new Date(source.getTime());
    }
    if (source instanceof RegExp) {
        return new RegExp(source.source, source.flags);
    }
    if (Array.isArray(source)) {
        return source.map(copy);
    }
    const result = {};
    Object.keys(source).forEach(key => {
        result[key] = copy(source[key]);
    });
    return result;
}

function bothNaN(a, b) {
    return typeof a === 'number' && typeof b === 'number' && isNaN(a) && isNaN(b);
}

export class Scope {
    constructor() {
        this.$id = nextId++;
        this.$$watchers = [];
        this.$$children = [];
        this.$$listeners = {};
        this.$$asyncQueue = [];
        this.$$postDigestQueue = [];
        this.$$phase = null;
        this.$$destroyed = false;
        this.$root = this;
        this.$parent = null;
        this.$$parse = null;
        this.$$scheduled = false;
    }

    $new() {
        const child = Object.create(this);
        child.$id = nextId++;
        child.$$watchers = [];
        child.$$children = [];
        child.$$listeners = {};
        child.$$destroyed = false;
        child.$parent = this;
        this.$$children.push(child);
        return child;
    }

    $$getter(expression) {
        if (typeof expression === 'function') {
            return expression;
        }
        return this.$root.$$parse(expression);
    }

    $watch(expression, listener, objectEquality) {
        const get = this.$$getter(expression);
        const watcher = {
            get,
            fn: typeof listener === 'function' ? listener : () => {},
            last: INIT,
            eq: Boolean(objectEquality),
            exp: expression,
        };
        this.$$watchers.unshift(watcher);
        this.$root.$$lastDirty = null;
        return () => {
            const index = this.$$watchers.indexOf(watcher);
            if (index >= 0) {
                this.$$watchers.splice(index, 1);
                this.$root.$$lastDirty = null;
            }
        };
    }

    // Поверхностное слежение за массивом или объектом: срабатывает при добавлении,
    // удалении и замене элементов, но не при изменении полей внутри элементов.
    $watchCollection(expression, listener) {
        const get = this.$$getter(expression);
        let oldValue;
        let changeDetected = 0;
        let snapshot;
        const detector = scope => {
            const newValue = get(scope);
            if (newValue === null || typeof newValue !== 'object') {
                if (snapshot !== newValue && !bothNaN(snapshot, newValue)) {
                    changeDetected++;
                }
                snapshot = newValue;
                oldValue = newValue;
                return changeDetected;
            }
            const items = Array.isArray(newValue) || (typeof newValue.length === 'number' && typeof newValue !== 'function')
                ? Array.prototype.slice.call(newValue)
                : null;
            if (items) {
                if (!Array.isArray(snapshot) || snapshot.length !== items.length
                    || items.some((item, index) => item !== snapshot[index] && !bothNaN(item, snapshot[index]))) {
                    changeDetected++;
                    snapshot = items;
                }
            } else {
                const keys = Object.keys(newValue).filter(key => key.charAt(0) !== '$' || key.charAt(1) !== '$');
                const old = snapshot && !Array.isArray(snapshot) && typeof snapshot === 'object' ? snapshot : null;
                const changed = !old || Object.keys(old).length !== keys.length
                    || keys.some(key => !Object.prototype.hasOwnProperty.call(old, key)
                        || (old[key] !== newValue[key] && !bothNaN(old[key], newValue[key])));
                if (changed) {
                    changeDetected++;
                    snapshot = {};
                    keys.forEach(key => {
                        snapshot[key] = newValue[key];
                    });
                }
            }
            oldValue = newValue;
            return changeDetected;
        };
        return this.$watch(detector, () => listener(oldValue, oldValue, this));
    }

    $digest() {
        const root = this.$root;
        if (root.$$phase) {
            throw new Error(`${root.$$phase} уже выполняется`);
        }
        root.$$phase = '$digest';
        root.$$lastDirty = null;
        let ttl = TTL;
        let dirty;
        try {
            do {
                dirty = false;
                while (root.$$asyncQueue.length) {
                    const task = root.$$asyncQueue.shift();
                    try {
                        task.scope.$eval(task.expression);
                    } catch (error) {
                        reportError(error);
                    }
                    // Задача могла изменить уже проверенное значение или набор наблюдателей.
                    root.$$lastDirty = null;
                }
                const stack = [this];
                traverseScopes:
                while (stack.length) {
                    const scope = stack.pop();
                    const watchers = scope.$$watchers;
                    for (let i = watchers.length - 1; i >= 0; i--) {
                        const watcher = watchers[i];
                        if (!watcher) {
                            continue;
                        }
                        try {
                            const value = watcher.get(scope);
                            const last = watcher.last;
                            let changed;
                            if (last === INIT) {
                                changed = true;
                            } else if (watcher.eq) {
                                changed = !equals(value, last);
                            } else {
                                changed = value !== last && !bothNaN(value, last);
                            }
                            if (changed) {
                                dirty = true;
                                root.$$lastDirty = watcher;
                                watcher.last = watcher.eq ? copy(value) : value;
                                watcher.fn(value, last === INIT ? value : last, scope);
                            } else if (root.$$lastDirty === watcher) {
                                // Как в AngularJS 1.2: после последнего изменённого наблюдателя
                                // остальные значения уже проверены на предыдущем проходе.
                                dirty = false;
                                break traverseScopes;
                            }
                        } catch (error) {
                            reportError(error);
                        }
                    }
                    for (let i = scope.$$children.length - 1; i >= 0; i--) {
                        stack.push(scope.$$children[i]);
                    }
                }
                if ((dirty || root.$$asyncQueue.length) && !(ttl--)) {
                    reportError(`Шаблон не успокоился за ${TTL} проходов проверки изменений.`);
                    break;
                }
            } while (dirty || root.$$asyncQueue.length);
        } finally {
            root.$$phase = null;
        }
        while (root.$$postDigestQueue.length) {
            try {
                root.$$postDigestQueue.shift()();
            } catch (error) {
                reportError(error);
            }
        }
    }

    $eval(expression, locals) {
        return this.$$getter(expression)(this, locals);
    }

    // Выполняет код и обновляет страницу. Внутри уже идущей проверки только выполняет код:
    // идущая проверка сама увидит изменения.
    $apply(expression) {
        const root = this.$root;
        try {
            return expression === undefined ? undefined : this.$eval(expression);
        } catch (error) {
            reportError(error);
            return undefined;
        } finally {
            if (!root.$$phase) {
                root.$digest();
            }
        }
    }

    $evalAsync(expression) {
        const root = this.$root;
        root.$$asyncQueue.push({ scope: this, expression });
        if (!root.$$phase && !root.$$scheduled) {
            root.$$scheduled = true;
            setTimeout(() => {
                root.$$scheduled = false;
                if (root.$$asyncQueue.length && !root.$$phase) {
                    root.$digest();
                }
            }, 0);
        }
    }

    $$postDigest(fn) {
        this.$root.$$postDigestQueue.push(fn);
    }

    $on(name, listener) {
        if (!this.$$listeners[name]) {
            this.$$listeners[name] = [];
        }
        this.$$listeners[name].push(listener);
        return () => {
            const list = this.$$listeners[name];
            const index = list ? list.indexOf(listener) : -1;
            if (index >= 0) {
                list.splice(index, 1);
            }
        };
    }

    $broadcast(name, ...args) {
        const event = {
            name,
            targetScope: this,
            currentScope: null,
            preventDefault() { event.defaultPrevented = true; },
            defaultPrevented: false,
        };
        const stack = [this];
        while (stack.length) {
            const scope = stack.pop();
            event.currentScope = scope;
            (scope.$$listeners[name] || []).slice().forEach(listener => {
                try {
                    listener(event, ...args);
                } catch (error) {
                    reportError(error);
                }
            });
            for (let i = scope.$$children.length - 1; i >= 0; i--) {
                stack.push(scope.$$children[i]);
            }
        }
        event.currentScope = null;
        return event;
    }

    $destroy() {
        if (this.$$destroyed || this === this.$root) {
            return;
        }
        this.$broadcast('$destroy');
        this.$$destroyed = true;
        this.$root.$$lastDirty = null;
        const siblings = this.$parent.$$children;
        const index = siblings.indexOf(this);
        if (index >= 0) {
            siblings.splice(index, 1);
        }
        this.$$watchers = [];
        this.$$children = [];
        this.$$listeners = {};
    }
}

export function createRootScope(parse) {
    const root = new Scope();
    root.$$parse = parse;
    return root;
}
